const currencyTools = require('./currencies');
const momentTz = require('moment-timezone');
const _ = require('lodash');

function getCurrencyCode(currencyId, options = { defaultCurrencyId: '840' }) {
    const defaultCurrencyId = _.get(options, 'defaultCurrencyId');
    let currency;

    if (currencyId || defaultCurrencyId) {
        currency = _.get(currencyTools.currencyMapper, currencyId) || _.get(currencyTools.currencyMapper, defaultCurrencyId);
    }

    return currency ? currency.code : '';
}

/**
 * Searches for creditcard-list strings and masks them. Uses Luhn algorithm to determine of string of digits is a valid credit card numbers
 *
 * @param {string} string - This is the target string that might contain an embedded credit card number
 * */
function maskEmbeddedCardNumber(string) {
    const stringToMask = _.isFinite(string) ? String(string) : string;
    if (!_.isString(stringToMask)) {
        return '';
    }
    const digitSequenceRegex = /(\d{4,6}( |-|_)?){3,4}/g;

    const matches = [...stringToMask.matchAll(digitSequenceRegex)];
    return matches.reduce((acc, match) => {
        const [matchingSubstring] = match;
        const trimmed = matchingSubstring.replace(/_| |-/g, '');
        if (isValidCreditCard(trimmed)) {
            if (/\s$/.test(matchingSubstring)) {
                const maskedNumber = maskNumber(trimmed).concat(' ');
                return acc.replace(matchingSubstring, maskedNumber);
            }
            return acc.replace(matchingSubstring, maskNumber(trimmed));
        } else {
            return acc;
        }
    }, stringToMask);
}

function isValidCreditCard(value) {
    const string = String(value);
    const withoutWhitespace = string.replace(/\s/g, '');
    const characters = withoutWhitespace.split('');
    const isAllZeros = characters.every((character) => {
        return character === '0';
    });
    if (isAllZeros) {
        return false;
    } else if (withoutWhitespace.length >= 12 && withoutWhitespace.length <= 19) {
        return luhnCheck(string);
    } else {
        return false;
    }
}

function maskNumber (cardNumber) {
    const trimmed = _.trim(cardNumber);
    if (_.isEmpty(trimmed)) {
        return cardNumber;
    }
    if (trimmed.length < 12) {
        return cardNumber;
    }
    const firstSix = trimmed.slice(0, 6);
    const lastFour = trimmed.slice(-4);
    const pad = _.pad('', trimmed.length - 10, '*');
    return `${firstSix}${pad}${lastFour}`;
}

function luhnCheck(value) {
    let stringifiedValue = value + '';
    if (/[^0-9-\s]+/.test(stringifiedValue)) return false;

    let nCheck = 0, bEven = false;
    stringifiedValue = stringifiedValue.replace(/\D/g, '');

    for (let n = stringifiedValue.length - 1; n >= 0; n--) {
        let cDigit = stringifiedValue.charAt(n);
        let nDigit = parseInt(cDigit, 10);

        if (bEven && (nDigit *= 2) > 9) nDigit -= 9;

        nCheck += nDigit;
        bEven = !bEven;
    }

    return (nCheck % 10) === 0;
}

function determineCardType(cardNumber) {
    let cardType = 'unknown';
    const cardTypeRegexes = {
        visa: /^4[0-9]{12}(?:[0-9]{3})?$/,
        masterCard: /^(5[1-5][0-9]{14}|2(22[1-9][0-9]{12}|2[3-9][0-9]{13}|[3-6][0-9]{14}|7[0-1][0-9]{13}|720[0-9]{12}))$/,
        americanExpress: /^3[47][0-9]{13}$/,
        discover: /^6(?:011|5[0-9]{2})[0-9]{12}$/,
        jcb: /^(?:2131|1800|35\d{3})\d{7,11}$/
    };

    for (let key in cardTypeRegexes) {
        if (cardTypeRegexes[key].test(cardNumber)) {
            cardType = key;
        }
    }

    return cardType;
}

const prettifyMoney = (strOrNum = '', currencyCode = 'USD', showCents = true) => {
    const currencyCodeLower = _.toLower(currencyCode);
    const currencyObj = _.get(currencyTools.currency, currencyCodeLower);
    const decimalsInCurrency = _.get(currencyObj, 'decimalPlaces');
    const number = +strOrNum;
    const decimalPlacesToUse = showCents ? decimalsInCurrency : 0;
    let moneyString = Math.abs(number).toLocaleString('en-US', {
        currency: currencyCode,
        currencyDisplay: 'symbol',
        style: 'currency',
        minimumFractionDigits: decimalPlacesToUse,
        maximumFractionDigits: decimalPlacesToUse
    });

    return number < 0 ? `(${moneyString})` : moneyString;
};

function prettifyNumber(number = 0, minimumFractionDigits = 0, maximumFractionDigits) {
    return number.toLocaleString(undefined, { minimumFractionDigits, maximumFractionDigits });
}

function prettifyCardNumber(strOrNum) {
    const input = strOrNum ? `${strOrNum}` : '';

    return input ? `${input.replace(/([X*]+)/gi, '...')}` : '';
}

function prettifyDateTimeV2(date, options = {}) {
    const momentObject = momentTz.utc(date).toObject();
    const dateTimeFormatInput = _.get(options, 'dateTimeFormatInput') || undefined;
    const dateTimeFormatOutput = _.get(options, 'dateTimeFormatOutput') || 'MM/DD/YYYY hh:mm A z';
    const timezoneOutput = _.get(options, 'timezoneOutput') || 'utc';

    const returnDateOnly = _.get(options, 'returnDateOnly') || false;
    const truncateZeroTimes = _.get(options, 'truncateZeroTimes') || false;

    if (returnDateOnly || (truncateZeroTimes && [momentObject.hours, momentObject.minutes, momentObject.seconds].every((val) => val === 0))) {
        const dateVsTimeSeparator = _.get(options, 'dateVsTimeSeperator') || ' ';
        const truncateZerosFormat = dateTimeFormatOutput.slice(0, dateTimeFormatOutput.indexOf(dateVsTimeSeparator));

        return momentTz.utc(date, dateTimeFormatInput).tz(timezoneOutput).format(truncateZerosFormat);
    } else {

        return momentTz.utc(date, dateTimeFormatInput).tz(timezoneOutput).format(dateTimeFormatOutput);
    }
}

function setIf(object, path, value) {
    if (!_.isNil(value)) {
        _.set(object, path, value);
    }
}

module.exports = {
    getCurrencyCode,
    maskEmbeddedCardNumber,
    isValidCreditCard,
    maskNumber,
    luhnCheck,
    determineCardType,
    prettifyMoney,
    prettifyNumber,
    prettifyCardNumber,
    prettifyDateTimeV2,
    setIf
};
